import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExitToApp,
  Search as SearchIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AppContext';
import UserAvatar from '../common/UserAvatar';
import Navigation from './Navigation';
import MobileBottomNav from './MobileBottomNav';
import CollegeService from '../../services/collegeService';
import { BaseComponentProps } from '../../types';

interface AppLayoutProps extends BaseComponentProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, className }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // State for mobile drawer and user menu
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Update document title with college name
  useEffect(() => {
    const collegeName = CollegeService.getCollegeName();
    document.title = `${collegeName} - Unii Club`;
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  if (!user) {
    return null; // Auth screens will be handled by routing
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
      className={className}
    >
      {/* App Bar */}
      <AppBar position="fixed" elevation={0}>
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo and title - Mobile-first Design */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2.5,
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 0.5, sm: 0.6 },
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              <SchoolIcon
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                  color: 'white',
                  mr: { xs: 0.75, sm: 1 },
                  flexShrink: 0
                }}
              />
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 0, sm: 0.75 },
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '0.8rem', sm: '0.95rem' },
                    letterSpacing: '0.02em',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    lineHeight: { xs: 1.3, sm: 1.5 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {CollegeService.getCollegeName()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                    flexShrink: 0
                  }}
                >
                  Unii Club
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Search */}
          <Tooltip title="Search People">
            <IconButton
              color="inherit"
              sx={{ mr: 1 }}
              onClick={() => navigate('/search')}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          {/* User menu */}
          <Tooltip title="Account">
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              sx={{ ml: 1 }}
              aria-controls={userMenuAnchor ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={userMenuAnchor ? 'true' : undefined}
            >
              <UserAvatar
                user={user}
                size="small"
                showRole={false}
              />
            </IconButton>
          </Tooltip>

          {/* User dropdown menu */}
          <Menu
            anchorEl={userMenuAnchor}
            id="account-menu"
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User info header */}
            <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {user.role} • {user.branch}
              </Typography>
            </Box>

            <Divider />

            <MenuItem onClick={handleLogout}>
              <ExitToApp fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main content area */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          pt: '64px', // Account for AppBar height
        }}
      >
        {/* Navigation sidebar */}
        <Navigation
          mobileOpen={mobileDrawerOpen}
          onMobileClose={() => setMobileDrawerOpen(false)}
        />

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: 'calc(100% - 240px)' },
            minHeight: 'calc(100vh - 64px)',
            pb: { xs: '80px', md: 0 }, // Add padding bottom on mobile for bottom nav
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              py: 3,
              px: { xs: 2, sm: 3 },
            }}
          >
            {children}
          </Container>
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </Box>
  );
};

export default AppLayout;